import { IconButton } from "@shared/components/IconButton";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useValidationContext } from "../../context";
import { useMeterReadingConsumption, useMeterReadingDetail } from "../../hooks";
import { ConsumptionHistory } from "./ConsumptionHistory";
import ImageCard from "./ImageCard";
import { InfoCard, InfoItem } from "./InfoCard";
import { ReadingDetailsCard } from "./ReadingDetailsCard";
import { ValidationActionButtons } from "./ValidationActionButtons";
import { ViewToggle } from "./ViewToggle";
import { ValueLabel } from "@features/ax/shared/ValueLabel";

type ValidationDetailsViewProps = {
  readingId: string;
};

function ValidationDetailsView({ readingId }: ValidationDetailsViewProps) {
  const {
    meterRunId,
    setCodeId,
    nextReadingId,
    setNextReadingId,
    validatorId,
    duplicateReadingId,
  } = useValidationContext();
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const isManualMeter = location.pathname.includes("manual-meter");

  const { data: detail } = useMeterReadingDetail({
    remoteUtilityId,
    meterRunId,
    meterReadingId: readingId,
    validatorId,
  });

  const nextReadingIdDetail = detail?.readingData?.previousDirectionId;
  const previousReadingId = detail?.readingData?.nextDirectionId;
  const meterNumber = detail?.meterNumber;

  useEffect(() => {
    if (detail) {
      setNextReadingId(nextReadingIdDetail);
    }
  }, [detail]);

  const { data: consumptionHistory } = useMeterReadingConsumption({
    remoteUtilityId,
    meterNumber,
  });

  const [selectedReadingId, setSelectedReadingId] = useState<any | null>(null);
  const [showPreviousData, setShowPreviousData] = useState<boolean>(true);

  const updateSelectedReadingId = (newId: any) => {
    if (newId === "NA") return;
    setSelectedReadingId(newId);
    searchParams.set("readingId", String(newId));
    setSearchParams(searchParams, { replace: true });
  };

  const statusCode =
    detail?.exemptCode ?? detail?.readingData?.reasonCode ?? "";
  setCodeId(detail?.readingData?.currentStatusCode);
  const readingDetails: InfoItem[][] = [
    [
      {
        label: "Previous Reading",
        value: detail?.readingData?.previousReading,
        subtext: (
          <>
            <Calendar size={14} className="inline mr-1" />
            {detail?.readingData?.previousReadingDate}
            <p className="text-xs text-primary pt-1">
              Status: {detail?.readingData?.previousStatus}
            </p>
          </>
        ),
      },
    ],
    [
      {
        label: "Current Reading",
        value: detail?.readingData?.currentReading,
        subtext: (
          <>
            <Calendar size={14} className="inline mr-1" />
            {detail?.readingData?.currentReadingDate}
            <p className="text-xs text-primary pt-1">
              Status: {detail?.readingData?.currentStatus}
            </p>
          </>
        ),
      },
    ],
    [
      {
        label: "Consumption",
        value: detail?.readingData?.consumption,
        subtext: `Avg. ${detail?.readingData?.avgConsumption} per day`,
      },
    ],
    [
      {
        label: "Days",
        value: detail?.readingData?.totalDays,
        subtext: "Between readings",
      },
    ],
  ];

  const meterInfo: InfoItem[][] = [
    [      
      { label: "Rsource ID", value: detail?.meterNumber },
      { label: "Type", value: detail?.resourceTypeDisplay },
      { label: "Physical Status", value: detail?.physicalStatus },
      { label: "Classification", value: detail?.ownershipDisplay },
    ],
    [
      { label: "Serial No", value: detail?.deviceNo },
      { label: "Sub Type", value: detail?.resourceSubTypeDisplay },
      { label: "Assignment Status", value: detail?.status },
    ],
  ];

  const meterReader: InfoItem[][] = [
    [
      {
        label: "FID",
        value: detail?.meterReader?.employeeId,
      },
      {
        label: "Uploaded Date",
        value: detail?.readingData?.currentReadingDate,
      },
    ],
    [
      {
        label: "Name",
        value: detail?.meterReader?.name,
      },
       {
        label: "Contact",
        value: detail?.meterReader?.contactNumber,
      },
    ],
  ];

  const consumerInfo: InfoItem[][] = [
    [
      { label: "Account Number", value: detail?.consumerNo },
      { label: "Account Type", value: detail?.accountType },
      { label: "Category", value: detail?.categoryName },
      { label: "Premise", value: detail?.premiseName },
      { label: "Service Address", value: detail?.address },

    ],
    [
      { label: "Name", value: detail?.firstName },
      { label: "Status", value: detail?.consumerStatus },
      { label: "Contact", value: detail?.contactNumber },
      { label: "Sub Category", value: detail?.subCategoryName },
    ],
  ];

  const suspiciousActivity: InfoItem[][] = [
    [
      {
        label: "Remark",
        value:
          detail?.readingData?.additionalData?.illegalActivityRemark ?? "N/A",
      },
    ],
    [
      {
        label: "Image",
        value:
          detail?.readingData?.additionalData?.illegalActivityPhoto ?? null,
        isLink: true,
      },
    ],
  ];

  const locationInfo: InfoItem[][] = [
    [
      { label: "Unit", value: detail?.unitCodeInfo?.name ?? "NA" },
      { label: "Premise", value: detail?.premiseName ?? detail?.premise ?? "NA" },
      { label: "Area", value: detail?.areaName ?? "NA" },
      { label: "Sub Area", value:detail?.subareaName ?? "NA" },
    ],
    [
      { label: "Lat", value: detail?.readingData?.latitude ?? "NA" },
      { label: "Long", value: detail?.readingData?.longitude ?? "NA" },
      {
        label: "Diff",
        value: detail?.readingData?.distance ?? "NA",
      },
    ],
  ];

  const validatorNameItems: InfoItem[] =
    Array.isArray(detail?.assignedValidators) &&
    detail.assignedValidators.length > 0
      ? detail.assignedValidators.map((v, index) => ({
          label: `Validator Name - #${index + 1}`,
          value: v.validatorName ?? "NA",
        }))
      : [
          {
            label: "Validator Name",
            value: detail?.validatedBy?.validatorName ?? "NA",
          },
        ];

  const validatorContactItems: InfoItem[] =
    Array.isArray(detail?.assignedValidators) &&
    detail.assignedValidators.length > 0
      ? detail.assignedValidators.map((v, index) => ({
          label: `Validator Contact - #${index + 1}`,
          value: v.email || v.contactNo || "NA",
        }))
      : [
          {
            label: "Validator Contact",
            value:
              detail?.validatedBy?.email ||
              detail?.validatedBy?.contactNumber ||
              "NA",
          },
        ];

  const readCycleInfo: InfoItem[][] = [
    [
      {
        label: "Cycle Name",
        value: detail?.meterCycleDetails?.meterCycleName ?? "NA",
      },
      {
        label: "Format",
        value:
          detail?.meterCycleDetails?.readFormat ??
          "NA",
      },
    ],
    [
      {
        label: "Route Name",
        value: detail?.routeDetails?.routeName ?? "NA",
      },
    ],
  ];

  return (
    <>
      {!readingId ? (
        <div className="flex items-center justify-between pb-4 w-full">
          <p className="text-gray-500 italic text-center flex-1">
            Select a reading to view details.
          </p>
          <ViewToggle />
        </div>
      ) : (
        <>
          <div className="flex justify-end items-center pb-4">
            <ViewToggle />
          </div>

          <div className="flex justify-end pb-4">
            <ValidationActionButtons
              details={detail}
              isManualMeter={isManualMeter}
            />
          </div>

          <div className="flex justify-between gap-6 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-y-6 gap-x-6 flex-1 pr-6">
              <ValueLabel label="Resource ID" value={detail?.meterNumber || "NA"} />
              <ValueLabel label="Serial No" value={detail?.deviceNo || "NA"} />
              <ValueLabel label="Physical Status" value={detail?.physicalStatus || "NA"} />
              <ValueLabel 
                label="Assignment Status" 
                value={detail?.status ? <Badge variant="success" className="font-semibold">{detail.status}</Badge> : "NA"} 
              />
              <ValueLabel label="Condition Status" value={detail?.readingData?.currentStatus || "NA"} />
              <ValueLabel label="VEE Status" value={detail?.readingData?.ruleStatus || "NA"} />
              <ValueLabel label="Validated By" value={detail?.validatedBy?.validatorName || "NA"} />
              <ValueLabel label="Validated On" value={detail?.validatedBy?.validationDate || "NA"} />
            </div>
            <div className="flex items-center gap-4">
              <IconButton
                label="Previous Reading"
                onClick={() =>
                  previousReadingId &&
                  updateSelectedReadingId(previousReadingId)
                }
                icon="ChevronLeft"
                disabled={!previousReadingId}
                variant="secondary"
                className="py-1.5 px-3 text-xs h-8"
              />
              <IconButton
                label="Next Reading"
                onClick={() =>
                  nextReadingId && updateSelectedReadingId(nextReadingId)
                }
                rightIcon="ChevronRight"
                disabled={!nextReadingId}
                variant="secondary"
                className="py-1.5 px-3 text-xs h-8"
              />
            </div>
          </div>

          <div className="w-full flex flex-col gap-4">
            <div className="grid grid-cols-7 w-full gap-4 items-stretch">
              {!isManualMeter && (
                <div className="col-span-2 h-full">
                  <ImageCard
                    imageUrl={"https://storage.googleapis.com/smart360-media-new/documents/prod/MX/29487487_20260603104650635558__a0dd2659-9ad5-4726-b0bf-f1a5f8ce60ac.png"}
                    previousReadingId={detail?.readingData?.previousReadingId}
                    showPrevious={showPreviousData}
                    currentImages={detail?.readingData?.currentReadingImages}
                  />
                </div>
              )}
              <div className={isManualMeter ? "col-span-7" : "col-span-5"}>
                <ReadingDetailsCard
                  detail={detail}
                  className="h-full"
                  showPreviousData={showPreviousData}
                  onShowPreviousDataChange={setShowPreviousData}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <InfoCard title="Resource Information" sections={meterInfo} />
              <InfoCard title="Consumer Info" sections={consumerInfo} />
              {detail?.meterReader?.id && (
                <InfoCard title="Meter Reader" sections={meterReader} />
              )}
              <InfoCard
                title="Read Cycle and Route Details"
                sections={readCycleInfo}
              />
              <InfoCard title="Location Information" sections={locationInfo} />
            <ConsumptionHistory
              data={
                consumptionHistory?.consumptionHistory
                  ?.slice(0, 5)
                  .map((item) => ({
                    month: item.billMonth,
                    value: item.totalConsumption,
                    color:
                      item.totalConsumption >
                      consumptionHistory?.avgConsumption * 1.5
                        ? "bg-red-500"
                        : "bg-blue-500",
                  })) ?? []
              }
            />
          </div>
            </div>
          
        </>
      )}
    </>
  );
}

export default ValidationDetailsView;
